import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Auth Module
import LoginPage from './modules/auth/pages/LoginPage';
import VerificationPage from './modules/auth/pages/VerificationPage';
import RegisterPage from './modules/auth/pages/RegisterPage';
import ForgotPasswordPage from './modules/auth/pages/ForgotPasswordPage';
import ResetPasswordPage from './modules/auth/pages/ResetPasswordPage';
import RoleSelectionPage from './modules/auth/pages/RoleSelectionPage';

// Layout Components
import AuthLayout from './components/layouts/AuthLayout';
import DashboardLayout from './components/layouts/DashboardLayout';

// Protected Route Component
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleBasedProtectedRoute from './components/auth/RoleBasedProtectedRoute';
import RoleSelectionRoute from './components/auth/RoleSelectionRoute';

// Unauthorized Access Page
import UnauthorizedAccessPage from './pages/UnauthorizedAccessPage';

// Dashboard Pages (placeholder for future modules)
import DashboardPage from './pages/DashboardPage';

// Admin Module
import { AdminDashboardPage, AdminCalendarPage, AdminPatientsPage, AdminNewsPage, AdminFaqsPage, AdminBookingPage, AdminClinicsPage, AdminDoctorsPage, AdminCliniciansPage, AdminUsersPage, AdminPatientFilesPage, AdminPatientDetailsPage, AdminPatientMedicalDetailsPage, AdminPatientBookingsPage, AdminPatientHistoryPage, AdminPatientAnalyticsPage, AdminPatientDocumentsPage } from './modules/admin/index';
import ConnexDeviceMonitor from './modules/admin/components/ConnexDeviceMonitor';
import ACVitalsMonitor from './modules/admin/pages/ACVitalsMonitor';
import ECGMonitorPage from './modules/admin/pages/ECGMonitorPage';
import ECGWorkstationManagementPage from './modules/admin/pages/ECGWorkstationManagementPage';
// Clinician Module  
import { ClinicianDashboardPage, ClinicianCalendarPage, ClinicianPatientFilesPage, ClinicianPatientDetailsPage, ClinicianPatientMedicalDetailsPage, ClinicianPatientBookingsPage, ClinicianAvailabilityPage, ClinicianEditAvailabilityPage, ClinicianNewsPage, ClinicianFaqsPage } from './modules/clinician/index';
import ClinicianAccountsPage from './modules/clinician/pages/ClinicianAccountsPage';
import ClinicianAccountsMainPage from './modules/clinician/pages/ClinicianAccountsMainPage';
import ClinicianPatientHistoryPage from './modules/clinician/pages/ClinicianPatientHistoryPage';
import ClinicianPatientAnalyticsPage from './modules/clinician/pages/ClinicianPatientAnalyticsPage';
import ClinicianPatientDocumentsPage from './modules/clinician/pages/ClinicianPatientDocumentsPage';
import ClinicianClinicalTelehealthNotesPage from './modules/clinician/pages/ClinicianClinicalTelehealthNotesPage';

// Doctor Module
import { DoctorDashboardPage, DoctorCalendarPage, DoctorPatientFilesPage, DoctorPatientDetailsPage, DoctorPatientMedicalDetailsPage, DoctorPatientBookingsPage, DoctorAvailabilityPage, DoctorEditAvailabilityPage, DoctorNewsPage, DoctorFaqsPage } from './modules/doctor/index';
import DoctorAccountsPage from './modules/doctor/pages/DoctorAccountsPage';
import DoctorAccountsMainPage from './modules/doctor/pages/DoctorAccountsMainPage';
import DoctorPatientHistoryPage from './modules/doctor/pages/DoctorPatientHistoryPage';
import DoctorPatientAnalyticsPage from './modules/doctor/pages/DoctorPatientAnalyticsPage';
import DoctorPatientDocumentsPage from './modules/doctor/pages/DoctorPatientDocumentsPage';
import DoctorGeneralTelehealthNotesPage from './modules/doctor/pages/DoctorGeneralTelehealthNotesPage';

// Patient Module
import PatientDashboardPage from './modules/patient/pages/PatientDashboardPage';
import PatientAdminPage from './modules/patient/pages/PatientAdminPage';
import PatientAdminLayout from './modules/patient/components/PatientAdminLayout';
import PatientPreferencesPage from './modules/patient/pages/PatientPreferencesPage';
import PatientAccountsPage from './modules/patient/pages/PatientAccountsPage';
import PatientBookingsPage from './modules/patient/pages/PatientBookingsPage';
import PatientVideoCallPage from './modules/patient/pages/PatientVideoCallPage';
import PatientNewsPage from './modules/patient/pages/PatientNewsPage';
import PatientFaqsPage from './modules/patient/pages/PatientFaqsPage';

function App() {
  return (
    <div className="App">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
      
      <Routes>
        {/* Auth Routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route index element={<Navigate to="/auth/login" replace />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="verification" element={<VerificationPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Role Selection Route (requires auth but no role selection) */}
        <Route path="/auth/role-selection" element={<RoleSelectionRoute />}>
          <Route index element={<RoleSelectionPage />} />
        </Route>

        {/* Unauthorized Access Page */}
        <Route path="/auth/unauthorized" element={<UnauthorizedAccessPage />} />

        {/* Protected Dashboard Routes */}
        <Route path="/" element={<ProtectedRoute />}>
          <Route element={<DashboardLayout />}>
            <Route index element={<DashboardPage />} />
            {/* Future module routes will be added here */}
          </Route>
        </Route>

        {/* ADMIN MODULE - Only admins can access */}
        <Route path="/admin" element={<RoleBasedProtectedRoute allowedRoles={['admin']} />}>
          <Route element={<DashboardLayout />}>
            <Route path="dashboard" element={<AdminDashboardPage />} />
            <Route path="booking" element={<AdminBookingPage />} />
            <Route path="patients" element={<AdminPatientFilesPage />} />
            <Route path="patientfiles" element={<AdminPatientFilesPage />} />
            <Route path="patientfiles/:patientId/general_details" element={<AdminPatientDetailsPage />} />
            <Route path="patientfiles/:patientId/medical_details" element={<AdminPatientMedicalDetailsPage />} />
            <Route path="patientfiles/:patientId/bookings" element={<AdminPatientBookingsPage />} />
            <Route path="patientfiles/:patientId/history" element={<AdminPatientHistoryPage />} />
            <Route path="patientfiles/:patientId/analytics" element={<AdminPatientAnalyticsPage />} />
            <Route path="patientfiles/:patientId/documents" element={<AdminPatientDocumentsPage />} />
            <Route path="clinics" element={<AdminClinicsPage />} />
            <Route path="doctors" element={<AdminDoctorsPage />} />
            <Route path="clinicians" element={<AdminCliniciansPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="connex-monitor" element={<ConnexDeviceMonitor />} />
            <Route path="ac-vitals-monitor" element={<ACVitalsMonitor />} />
            <Route path="ecg-monitor" element={<ECGMonitorPage />} />
            <Route path="ecg-workstations" element={<ECGWorkstationManagementPage />} />
            <Route path="news" element={<AdminNewsPage />} />
            <Route path="faqs" element={<AdminFaqsPage />} />
          </Route>
        </Route>

        {/* DOCTOR MODULE - Only doctors can access */}
        <Route path="/doctor" element={<RoleBasedProtectedRoute allowedRoles={['doctor']} />}>
          <Route element={<DashboardLayout />}>
            <Route path="dashboard" element={<DoctorDashboardPage />} />
            <Route path="calendar" element={<DoctorCalendarPage />} />
            <Route path="patientfiles" element={<DoctorPatientFilesPage />} />
                            <Route path="patientfiles/:patientId/general_details" element={<DoctorPatientDetailsPage />} />
                <Route path="patientfiles/:patientId/medical_details" element={<DoctorPatientMedicalDetailsPage />} />
                <Route path="patientfiles/:patientId/bookings" element={<DoctorPatientBookingsPage />} />
                <Route path="patientfiles/:patientId/history" element={<DoctorPatientHistoryPage />} />
                <Route path="patientfiles/:patientId/analytics" element={<DoctorPatientAnalyticsPage />} />
                <Route path="patientfiles/:patientId/documents" element={<DoctorPatientDocumentsPage />} />
            <Route path="availability/current" element={<DoctorAvailabilityPage />} />
            <Route path="availability/edit" element={<DoctorEditAvailabilityPage />} />
            <Route path="accounts/rcti" element={<DoctorAccountsPage />} />
            <Route path="accounts" element={<DoctorAccountsMainPage />} />
            <Route path="patients" element={<DoctorDashboardPage />} />
            <Route path="news" element={<DoctorNewsPage />} />
            <Route path="faqs" element={<DoctorFaqsPage />} />
            <Route path="ac-vitals-monitor" element={<ACVitalsMonitor />} />
          </Route>
          {/* Doctor General Telehealth Notes - No Navigation Bar (Secure with booking ID) */}
          <Route path="generaltelenotes/:booking_id" element={<DoctorGeneralTelehealthNotesPage />} />
          
          {/* Doctor Clinical Telehealth Notes - Same component, different URL (Secure with booking ID) */}
          <Route path="clinicaltelenotes/:booking_id" element={<DoctorGeneralTelehealthNotesPage />} />
        </Route>

        {/* PATIENT MODULE - Only patients can access */}
        <Route path="/patient" element={<RoleBasedProtectedRoute allowedRoles={['patient']} />}>
          <Route element={<DashboardLayout />}>
            <Route path="dashboard" element={<PatientDashboardPage />} />
            <Route path="news" element={<PatientNewsPage />} />
            <Route path="faqs" element={<PatientFaqsPage />} />
          </Route>
          <Route path="admin" element={<PatientAdminLayout />}>
            <Route path="mydetails" element={<PatientAdminPage />} />
            <Route path="preferences" element={<PatientPreferencesPage />} />
            <Route path="accounts" element={<PatientAccountsPage />} />
            <Route path="bookings" element={<PatientBookingsPage />} />
            <Route index element={<Navigate to="/patient/admin/mydetails" replace />} />
          </Route>
          {/* Patient Video Call - No Navigation Bar (Secure with booking ID) */}
          <Route path="video-call/:booking_id" element={<PatientVideoCallPage />} />
        </Route>

        {/* CLINICIAN MODULE - Only clinicians can access */}
        <Route path="/clinician" element={<RoleBasedProtectedRoute allowedRoles={['clinician']} />}>
          <Route element={<DashboardLayout />}>
            <Route path="dashboard" element={<ClinicianDashboardPage />} />
            <Route path="calendar" element={<ClinicianCalendarPage />} />
            <Route path="patientfiles" element={<ClinicianPatientFilesPage />} />
                            <Route path="patientfiles/:patientId/general_details" element={<ClinicianPatientDetailsPage />} />
                <Route path="patientfiles/:patientId/medical_details" element={<ClinicianPatientMedicalDetailsPage />} />
                <Route path="patientfiles/:patientId/bookings" element={<ClinicianPatientBookingsPage />} />
                <Route path="patientfiles/:patientId/history" element={<ClinicianPatientHistoryPage />} />
                <Route path="patientfiles/:patientId/analytics" element={<ClinicianPatientAnalyticsPage />} />
                <Route path="patientfiles/:patientId/documents" element={<ClinicianPatientDocumentsPage />} />
            <Route path="availability/current" element={<ClinicianAvailabilityPage />} />
            <Route path="availability/edit" element={<ClinicianEditAvailabilityPage />} />
            <Route path="accounts/rcti" element={<ClinicianAccountsPage />} />
            <Route path="accounts" element={<ClinicianAccountsMainPage />} />
            <Route path="patients" element={<ClinicianDashboardPage />} />
            <Route path="news" element={<ClinicianNewsPage />} />
            <Route path="faqs" element={<ClinicianFaqsPage />} />
            <Route path="ac-vitals-monitor" element={<ACVitalsMonitor />} />
          </Route>
          {/* Clinician Clinical Telehealth Notes - No Navigation Bar (Secure with booking ID) */}
          <Route path="clinicaltelenotes/:booking_id" element={<ClinicianClinicalTelehealthNotesPage />} />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/auth/login" replace />} />
      </Routes>
    </div>
  );
}

export default App; 